<!-- eslint-disable max-lines -->
<template>
  <div class="wrapper register-steps">
    <header>
      <div class="expanded row">
        <UiToolbar
          v-sticky
          class="columns small-12"
          data-sticky-target=".ui-toolbar"
          data-sticky-class="ui-toolbar--shadowed"
          data-sticky-top-offset="0"
          :title="$t('AUTH.STEPS.TITLE')"
          @navIconClicked="changeVisibility(true)"
        >
          <div slot="actions" class="show-for-large">
            <div class="ui-toolbar__links">
              <a
                class="ui-toolbar__link ui-toolbar__link--latin"
                role="button"
                @click.prevent="signOut"
              >
                {{ $t('AUTH.STEPS.LOGOUT') }}
              </a>

              <LocaleDropdown class="register-steps__locale-dropdown" />
            </div>
          </div>

          <div class="profile hide-for-large">
            <a
              class="ui-toolbar__link ui-toolbar__link--latin"
              role="button"
              @click.prevent="signOut"
            >
              {{ $t('AUTH.STEPS.LOGOUT') }}
            </a>
            <LocaleDropdown class="register-steps__locale-dropdown" />
          </div>
        </UiToolbar>
      </div>
    </header>
    <div class="auth-steps">
      <div class="row align-center">
        <div class="auth-steps__steps column small-12">
          <ElSteps
            ref="steps"
            :active="active"
            align-center
            finish-status="success"
            space
          >
            <ElStep :title="$t('AUTH.STEPS.ACCOUNT_TYPE')" />
            <ElStep :title="$t('AUTH.STEPS.ACCOUNT_INFORMATION')" />
            <ElStep :title="$t('AUTH.STEPS.PUBLIC_PROFILE')" />
            <ElStep
              v-if="isDomestic"
              :title="$t('AUTH.STEPS.IDENTITY_VERIFICATION')"
            />
            <ElStep :title="$t('AUTH.STEPS.DONE')" />
          </ElSteps>
        </div>
        <div class="auth-steps__contents column small-12 large-10">
          <template v-if="active === 0">
            <Transition name="fade-transform" mode="out-in">
              <AccountType
                :loading="loading"
                @nextStep="nextStep"
                @prevStep="prevStep"
                @submitAccountType="submitAccountType"
              />
            </Transition>
          </template>

          <template v-if="active === 1">
            <Transition name="fade-transform" mode="out-in">
              <template>
                <DomesticNatural
                  v-if="isDomesticNatural"
                  :email="tokenEmail"
                  :errors="nextStepErrors"
                  :loading="loading"
                  @nextStep="nextStep"
                  @prevStep="prevStep"
                  @submitAccountInformation="submitAccountInformation"
                />

                <ForeignerNatural
                  v-else-if="isForeignerNatural"
                  :email="tokenEmail"
                  :errors="nextStepErrors"
                  :loading="loading"
                  @nextStep="nextStep"
                  @prevStep="prevStep"
                  @submitAccountInformation="submitAccountInformation"
                />

                <DomesticLegal
                  v-else-if="isDomesticLegal"
                  :email="tokenEmail"
                  :errors="nextStepErrors"
                  :loading="loading"
                  @nextStep="nextStep"
                  @prevStep="prevStep"
                  @submitAccountInformation="submitAccountInformation"
                />

                <ForeignerLegal
                  v-else-if="isForeignerLegal"
                  :email="tokenEmail"
                  :errors="nextStepErrors"
                  :loading="loading"
                  @nextStep="nextStep"
                  @prevStep="prevStep"
                  @submitAccountInformation="submitAccountInformation"
                />
              </template>
            </Transition>
          </template>

          <template v-if="active === 2">
            <Transition name="fade-transform" mode="out-in">
              <PublicProfile
                :errors="nextStepErrors"
                :loading="loading"
                @nextStep="nextStep"
                @prevStep="prevStep"
                @submitPublicProfile="submitPublicProfile"
              />
            </Transition>
          </template>

          <template v-if="active === 3">
            <Transition name="fade-transform" mode="out-in">
              <IdentifyVerification
                :errors="nextStepErrors"
                :loading="loading"
                :default-phone="accountInformation && accountInformation.phone"
                @nextStep="nextStep"
                @prevStep="prevStep"
                @submitSmsVerification="submitSmsVerification"
              />
            </Transition>
          </template>

          <template v-if="active === 4">
            <Done />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { Register } from '@/api/api';
import { loginWithTokenAndFetch } from '../../../auth';

import AccountType from './AccountType';
import DomesticNatural from './DomesticNatural';
import DomesticLegal from './DomesticLegal';
import ForeignerLegal from './ForeignerLegal';
import ForeignerNatural from './ForeignerNatural';
import PublicProfile from './PublicProfile';
import IdentifyVerification from './IdentifyVerification';
import Done from './Done';

import UiToolbar from '@@/layout/UiToolbar';
import LocaleDropdown from '@@/common/LocaleDropdown'

import sticky from '../../../directives/sticky';

import form from '@/mixins/form';
import lang from '@/mixins/lang';

import ErrorsList from '@@/common/ErrorsList';

const nameToStepNumber = {
  'REGISTER-PUBLISHER': 0,
  'REGISTER-DEVELOPER-TITLE': 2,
  'VERIFY-PHONE': 3,
  'DONE': 4,
};

export default {
  name: 'RegisterSteps',

  directives: {
    sticky,
  },

  components: {
    Done,
    ErrorsList,
    AccountType,
    UiToolbar,
    DomesticNatural,
    DomesticLegal,
    ForeignerNatural,
    ForeignerLegal,
    PublicProfile,
    IdentifyVerification,
    LocaleDropdown
  },

  mixins: [form,lang],

  data() {
    return {
      active: 0,
      stepsSize: 5,
      nextStepErrors: null,

      accountType: {
        country: 'IR',
        type: 'natural'
      },
      accountInformation: null,
      publicProfile: null,
      loading: false,

    };
  },

  computed: {


    tokenEmail() {
      return this.$auth.user?.email;
    },


    isNatural() {
      return this.accountType.type === 'natural';
    },

    isDomestic() {
      return this.accountType.country === 'IR';
    },

    isDomesticNatural() {
      return this.isNatural && this.isDomestic;
    },

    isForeignerNatural() {
      return this.isNatural && !this.isDomestic;
    },

    isDomesticLegal() {
      return !this.isNatural && this.isDomestic;
    },

    isForeignerLegal() {
      return !this.isNatural && !this.isDomestic;
    },
  },

  created() {
    const { signupState, publisher } = this.$auth.user;

    if(publisher) {
      const { accountType, country } = publisher
      const type = accountType === 'N' ? 'natural' : 'legal';
      this.accountType = {
        type,
        country
      }
    }
    this.active = nameToStepNumber[signupState];
  },

  methods: {

    startLoading() {
      this.loading = true;
    },
    finishLoading() {
      this.loading = false;
    },

    signOut() {
      this.$router.replace({ name: 'logout' });
    },

    nextStep() {
      this.nextStepErrors = null;
      if (this.active === this.stepsSize - 1) {
        this.active = 0;
      }
      if (
        !this.isDomestic &&
        this.active === nameToStepNumber['REGISTER-DEVELOPER-TITLE']
      ) {
        this.active = nameToStepNumber.DONE;
      } else {
        this.active += 1;
      }

      this.calcStepsPosition();
    },

    prevStep() {
      if (this.active === 0) {
        return;
      }

      this.active -= 1;

      this.calcStepsPosition();
    },

    submitAccountType(accountType) {
      this.accountType = Object.assign({}, accountType);
      this.nextStep();
    },

    getFileId(file) {
      if (file) {
        return file.id;
      }
    },

    getSubmissionData(accountInformation) {
      this.accountInformation = Object.assign({}, accountInformation);
      const { type, country } = this.accountType;
      const {
        name,
        province,
        address,
        postal_code,
        phone,
        city,

        national_code,
        national_card_image,

        passport_code,
        passport_image,

        national_id,
        economic_code,

        registration_number, // foreigner legal
        company_certificate, // id,url
        vat_image,
        ceo_national_card, // id,url
      } = this.accountInformation;

      return {
        account_type: type[0].toUpperCase(),
        country,
        email: this.$auth.user.email,

        identification_info: {
          name,
          national_code,
          national_card_image: this.getFileId(national_card_image),

          national_id,
          economic_code,

          passport_code,
          passport_image: this.getFileId(passport_image),

          registration_number,
          company_certificate: this.getFileId(company_certificate),
          vat_image: this.getFileId(vat_image),
          ceo_national_card: this.getFileId(ceo_national_card),
        },
        contact_info: {
          province,
          address,
          postal_code,
          phone: phone.replace(/\D+/g, ''),
          city,
        },
      };
    },

    mergeErrorsHandler({ body: { message, errors } }) {
      if (message) {
        this.$message.error(message);
      }

      if (errors) {
        this.nextStepErrors = Object.assign(
          {},
          errors,
          errors.identification_info,
          errors.contact_info,
        );
      }
    },

    submitAccountInformation(accountInformation) {
      this.nextStepErrors = null;
      this.startLoading();
      const data = this.getSubmissionData(accountInformation);
      return Register.registerPublisher({}, data)
        .then(({ body: { publisher, token, type, message } }) => {
          if (message) {
            this.$message.success(message);
          }

          this.nextStep();
          return this.$auth.loginWithToken(token);
        })
        .catch(this.mergeErrorsHandler)
        .finally(this.finishLoading);
    },

    errorHandler(error) {
      const { body: { errors, message } = {} } = error;
      if (message) {
        this.$message.error(message);
      }

      if (errors) {
        this.nextStepErrors = errors;
      }

      // To identify errors on the sentry
      if(!message && !errors) {
        this.$sentry.captureException(error);
      }
    },

    submitPublicProfile(publicProfile) {
      this.nextStepErrors = null;
      this.publicProfile = Object.assign({}, publicProfile);

      this.startLoading();
      return Register.registerDeveloper({}, this.publicProfile)
        .then(({ body: { token, type, message } }) => {
          if (message) {
            this.$message.success(message);
          }

          this.nextStep();
          return this.$auth.loginWithToken(token);
        })
        .catch(this.errorHandler)
        .finally(this.finishLoading);
    },

    submitSmsVerification(code) {
      this.nextStepErrors = null;
      this.startLoading();
      return Register.smsVerify({ verification_code: code })
        .then(({ body: { message, token } }) => {
          if (message) {
            this.$message.success(message);
          }

          this.nextStep();
          localStorage.removeItem('registration-sms-sent');
          return this.$auth.loginWithToken(token);
        })
        .catch(this.errorHandler)
        .finally(this.finishLoading);
    },

    calcStepsPosition() {
      const activeElement = this.$refs.steps.$el.children[this.active];
      const scrollDiff =
        this.$refs.steps.$el.scrollWidth - this.$refs.steps.$el.clientWidth;
      let scrollAmount;

      if (this.lang !== 'fa') {
        scrollAmount =
          activeElement.offsetLeft -
          this.$refs.steps.$el.clientWidth / 2 +
          activeElement.clientWidth / 2;
      } else {
        scrollAmount =
          activeElement.offsetLeft +
          scrollDiff -
          this.$refs.steps.$el.clientWidth / 2 +
          activeElement.clientWidth / 2;
      }

      this.$refs.steps.$el.scrollLeft = scrollAmount;
    },
  },
};
</script>

<style lang="scss">
.register-steps {
  &__locale-dropdown {
    margin-left: $global-margin;
    @include rtl() {
      margin-left: 0;
      margin-right: $global-margin;
    }
  }
}
</style>


